import { unwrapApiData } from '@/api/unwrapApiResult'
import {
  type AccessToken,
  isExpired,
  parseAccessToken,
  tokenRepository,
} from '@/auth/tokens'

import { authRefresh } from './authApi'
import { type UserDto, userService } from './userService'

export interface AuthorizeUserResponse {
  accessToken: string
  refreshToken: string
  userData: UserDto
}

// Deduplicate concurrent refreshes — parallel requests would otherwise each
// burn the refresh token once.
let refreshPromise: Promise<AccessToken> | null = null

async function refreshAccessToken(refreshToken: string): Promise<AccessToken> {
  const result = await authRefresh(refreshToken)
  const data = unwrapApiData(result, 'Failed to refresh session')
  const accessToken = parseAccessToken(data.access_token)
  tokenRepository.save(accessToken.value, refreshToken)
  return accessToken
}

class AuthService {
  saveAuthResponse(data: AuthorizeUserResponse): void {
    userService.saveUser(data.userData)
    tokenRepository.save(data.accessToken, data.refreshToken)
  }

  logout(): void {
    tokenRepository.clear()
    userService.clearUser()
  }

  async getTokens(options?: { forceRefresh?: boolean }): Promise<{
    accessToken: AccessToken
    refreshToken: string
  }> {
    const stored = tokenRepository.load()
    if (!stored) throw new Error('No session found')

    const { refreshToken, accessToken } = stored
    if (!options?.forceRefresh && !isExpired(accessToken)) {
      return { accessToken, refreshToken }
    }

    refreshPromise ??= refreshAccessToken(refreshToken).finally(() => {
      refreshPromise = null
    })
    return { accessToken: await refreshPromise, refreshToken }
  }
}

export const authService = new AuthService()
